import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, string } from 'prop-types';
import take from 'lodash/take';
import classnames from 'classnames';
import { numberWithCommas } from 'ps-components/lib/formatters';
import LoadingIndicator from 'ps-components/LoadingIndicator/LoadingIndicator';
import SkillRow from './SkillRow';
import SubHeaderSearch from '../../JobMatching/ResultDetails/SubHeaderSearch';
import Table from 'ps-components/Table/Table/Table';
import TableBody from 'ps-components/Table/TableBody/TableBody';
import TableHeader from 'ps-components/Table/TableHeader/TableHeader';
import Popover from 'ps-components/Popover/Popover';
import './SkillTable.scss';

const COLUMNS = [' ', 'Skills', '% Reporting Skills', 'Suggested Skill Differential'];
const SEARCH_JOB_LINK_TEXT = 'Search for a different job title match';
const SEARCH_JOB_NOTE = "Don't see the skill you are looking for?";
const SEARCH_PLACEHOLDER = 'Search for a skill within current job title match';
const SELECTED_JOB_NOTE_TEXT = 'Popular skills are based on PayScale job title match';

class SkillTable extends Component {
  static propTypes = {
    openJobSearch: func,
    sampleSize: number,
    selectedJobTitle: string,
    skillsLoaded: bool,
    tasks: arrayOf(string),
    skills: array,
    selectedSkills: array,
    toggleSkill: func,
    featureIsPurchased: bool
  };

  constructor(props) {
    super(props);

    this.state = {
      isSelected: true,
      limit: 5,
      searchValue: '',
      showTasks: false,
      skills: []
    };
  }

  componentDidMount() {
    const { skills } = this.props;

    this.setState({ skills: skills });
  }

  componentDidUpdate(prevProps, prevState) {
    const { skills } = this.props;
    const { searchValue } = this.state;

    if (skills.length > prevProps.skills.length) this.setState({ skills: skills });
    if (searchValue !== prevState.searchValue) this.searchSkills();
  }

  componentWillUnmount() {
    this.setState({
      isSelected: true,
      limit: 5,
      searchValue: '',
      showTasks: false,
      skills: []
    });
  }

  clearSearchText = () => {
    this.setState({ searchValue: '' });
    this.searchSkills();
  };

  filterSkills = () => {
    const { skills } = this.props;
    const { searchValue } = this.state;
    const formattedSearchValue = searchValue.toLowerCase();

    return skills.filter(skill => skill.skill.toLowerCase().includes(formattedSearchValue));
  };

  getTotalCount = () => {
    const { skills } = this.props;

    return skills.reduce((total, skill) => {
      return (total += skill.count);
    }, 0);
  };

  renderLoadingIndicator = () => {
    const { skillsLoaded } = this.props;

    return (
      <div className="card__row skill__search-row loading">
        <LoadingIndicator loading={!skillsLoaded} />
      </div>
    );
  };

  renderPagination = () => {
    const { isSelected, limit, skills } = this.state;
    const { selectedSkills } = this.props;
    const skillsToRender = isSelected ? skills : skills.filter(skill => selectedSkills.includes(skill.skill));

    if (skillsToRender.length < limit || !isSelected) return;

    return (
      <div className="card__row">
        <a className="skills__pagination-link" onClick={skillsToRender.length > limit ? this.showMore : this.showLess}>
          {`Show ${skillsToRender.length > limit ? 'More' : 'Less'} Skills`}
        </a>
      </div>
    );
  };

  renderPayScaleMatchAndSearch = () => {
    const { openJobSearch, selectedJobTitle, featureIsPurchased, tasks } = this.props;
    const { showTasks } = this.state;

    return (
      <div className="card__row location__title-search">
        <div className="left__panel">
          <p className="location__search-note">{SELECTED_JOB_NOTE_TEXT}</p>
          <div className="location__search-job">
            <span className="location-search__job-title">
              {!featureIsPurchased ? 'Sample - Senior Software Engineer' : selectedJobTitle}
            </span>{' '}
            <a onClick={this.toggleShowTasks}>(view tasks)</a>
            {showTasks && (
              <Popover togglePopover={this.toggleShowTasks} display={showTasks} popoverText={tasks?.join(' ')} />
            )}
          </div>
          <div className="location__search">
            <span className="location__search-label">Location</span>
            <p className="selected__location">United States (Natl)</p>
          </div>
        </div>
        <div className="divider" />
        <div className="right__panel">
          <div className="skill-search-help-note">{SEARCH_JOB_NOTE}</div>
          <a className={!featureIsPurchased ? 'search__job-link-disabled' : 'search__job-link'} onClick={openJobSearch}>
            {SEARCH_JOB_LINK_TEXT}
          </a>
        </div>
      </div>
    );
  };

  renderRow = skill => {
    const { selectedSkills, toggleSkill } = this.props;

    return <SkillRow key={skill.skill} selectedSkills={selectedSkills} skill={skill} toggleSkill={toggleSkill} />;
  };

  renderSkills = () => {
    return (
      <React.Fragment>
        <div className="card__row skill__search-row">{this.renderSkillsSearch()}</div>
        <div className="card__row card__table-row">{this.renderSkillsTable()}</div>
      </React.Fragment>
    );
  };

  renderSkillsSearch = () => {
    const { sampleSize, selectedSkills } = this.props;
    const { isSelected, searchValue, skills } = this.state;

    return (
      <React.Fragment>
        <SubHeaderSearch
          clearSearch={this.clearSearchText}
          isSelected={isSelected}
          placeholderText={SEARCH_PLACEHOLDER}
          searchId="skills_search"
          searchByText={this.searchSkills}
          searchText={searchValue}
          selectedItems={selectedSkills}
          setSearchText={this.setSearchText}
          showSelectAllText={false}
          toggleSelected={this.toggleTabSelection}
          totalCount={skills.length}
        />
        {sampleSize && (
          <div className="sample__size-note">{`Sample Size: ${numberWithCommas(sampleSize)} profiles`}</div>
        )}
      </React.Fragment>
    );
  };

  renderSkillsTable = () => {
    const { isSelected, limit, skills } = this.state;
    const { selectedSkills } = this.props;
    const skillsToRender = isSelected
      ? take(skills, limit)
      : skills.filter(skill => selectedSkills.includes(skill.skill));
    const cellClass = classnames({
      'skill__table-header-cell': true,
      scrollable__padding: (isSelected && skills.length === limit) || (!isSelected && skillsToRender.length > 5)
    });

    return (
      <Table className="skill__table" hasStickyColumns={true}>
        <TableHeader cellClass={cellClass} columns={COLUMNS} rowClass="skill__table-header-row" />
        <TableBody className="skill__differential-table-body">
          {skillsToRender.map(skill => this.renderRow(skill))}
        </TableBody>
      </Table>
    );
  };

  searchSkills = () => {
    const { skills } = this.props;
    const { searchValue } = this.state;
    const newSkills = searchValue ? this.filterSkills() : skills;

    this.setState({ skills: newSkills });
  };

  setSearchText = value => {
    this.setState({ searchValue: value });
  };

  showLess = () => {
    const { skills } = this.props;

    this.setState({ limit: 5 });
  };

  showMore = () => {
    const { skills } = this.props;

    this.setState({ limit: skills.length });
  };

  toggleShowTasks = (event) => {
    event?.stopPropagation();
    const { showTasks } = this.state;
    this.setState({ showTasks: !showTasks });
  };

  toggleTabSelection = () => {
    const { isSelected } = this.state;

    this.setState({ isSelected: !isSelected });
  };

  render() {
    const { skillsError, skillsLoaded } = this.props;
    const { skills, limit } = this.state;
    const skillsTableSection = !skillsLoaded ? this.renderLoadingIndicator() : this.renderSkills();

    return (
      <React.Fragment>
        {this.renderPayScaleMatchAndSearch()}
        {skillsTableSection}
        {this.renderPagination()}
      </React.Fragment>
    );
  }
}

export default SkillTable;
